import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/stores/auth';
import { BookingSafetyPanel } from '@/components/booking/BookingSafetyPanel';
import { Button } from '@/components/ui/Button';
import { Textarea } from '@/components/ui/Input';
import { toast } from '@/components/ui/Toast';
import { callRaisePanic } from '@/lib/callables';
import { NATIONAL_EMERGENCY, PANIC_SCREEN } from '@makhe/shared';

export default function CampusControlPage() {
  const { userDoc } = useAuth();
  const [params] = useSearchParams();
  const bookingId = params.get('bookingId') ?? '';
  const [note, setNote] = useState('');
  const [shareLocation, setShareLocation] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  if (!userDoc) return null;

  const raise = async () => {
    setBusy(true);
    try {
      let location: { latitude: number; longitude: number } | undefined;
      if (shareLocation && 'geolocation' in navigator) {
        location = await new Promise((resolve) => {
          navigator.geolocation.getCurrentPosition(
            (pos) =>
              resolve({ latitude: pos.coords.latitude, longitude: pos.coords.longitude }),
            () => resolve(undefined),
            { enableHighAccuracy: false, timeout: 8000 },
          );
        });
      }
      await callRaisePanic({
        bookingId: bookingId || undefined,
        note: note.trim() || undefined,
        shareLocation,
        location,
      });
      setSent(true);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="mx-auto max-w-2xl px-5 py-6 pb-16 md:px-6">
      <p className="eyebrow mb-2">Campus Control</p>
      <h1 className="font-display text-3xl">Get help</h1>
      <p className="mt-2 text-sm text-makhe-muted">
        Makhe is not the police and not campus security. Use the numbers first if you are in
        danger. The rest of this page is how Makhe can back you up.
      </p>

      <div className="mt-5 rounded-xl border border-red-500/30 bg-red-500/5 p-4">
        <p className="font-medium">In immediate danger?</p>
        <p className="mt-1 text-sm text-makhe-muted">Leave if you can. Then call:</p>
        <div className="mt-3 flex flex-wrap gap-2">
          <a href={`tel:${NATIONAL_EMERGENCY.saps}`}>
            <Button type="button" variant="danger" size="sm">
              SAPS {NATIONAL_EMERGENCY.saps}
            </Button>
          </a>
          <a href={`tel:${NATIONAL_EMERGENCY.ambulance}`}>
            <Button type="button" variant="secondary" size="sm">
              Ambulance {NATIONAL_EMERGENCY.ambulance}
            </Button>
          </a>
        </div>
      </div>

      <div className="mt-6">
        <BookingSafetyPanel buyerInstitution={userDoc.studentVerification?.institution} />
      </div>

      <section className="mt-8">
        <h2 className="font-display text-xl">What Makhe already does</h2>
        <ul className="mt-3 space-y-2 text-sm text-makhe-ink/80">
          <li>Both sides must be verified students before a booking.</li>
          <li>Late-night sessions ask each of you to check in.</li>
          <li>You can cancel for safety with no penalty, and report from a booking.</li>
          <li>Every booking is a record operators can open later.</li>
        </ul>
        <div className="mt-4 flex flex-wrap gap-3 text-sm">
          <Link to="/bookings" className="text-makhe-primary underline">
            Open bookings
          </Link>
          <Link to="/profile/verify-student" className="text-makhe-primary underline">
            Student verification
          </Link>
        </div>
      </section>

      <section className="mt-10 rounded-xl border border-makhe-line p-4">
        <h2 className="font-display text-xl">{PANIC_SCREEN.title}</h2>
        <p className="mt-2 text-sm text-makhe-muted">{PANIC_SCREEN.notEmergency}</p>
        <p className="mt-2 text-sm text-makhe-ink/80">{PANIC_SCREEN.body}</p>

        {sent ? (
          <p className="mt-4 text-sm font-medium text-makhe-forest">{PANIC_SCREEN.sent}</p>
        ) : (
          <>
            {bookingId && (
              <p className="mt-3 text-xs text-makhe-muted">
                This alert will include the booking you opened this from.
              </p>
            )}
            <Textarea
              className="mt-3"
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Anything operators should know (optional)"
            />
            <label className="mt-3 flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                className="mt-1"
                checked={shareLocation}
                onChange={(e) => setShareLocation(e.target.checked)}
              />
              <span>Share my location with Makhe for this alert only. Off by default.</span>
            </label>
            <Button
              className="mt-4 w-full"
              variant="danger"
              loading={busy}
              onClick={() => void raise()}
            >
              {PANIC_SCREEN.confirm}
            </Button>
          </>
        )}
      </section>
    </section>
  );
}
